import type { ReactNode } from 'react'

import { IMAGE_RESIZE_SPEC } from '@core/image'
import { Avatar, AvatarFallback, AvatarImage, Link } from '@ds/components'

import styles from './AudioHeroBar.module.css'

type TAudioHeroBarProps = {
    authorName: string
    authorHref?: string
    avatarUrl?: string
    /** Replaces the avatar image, for the SSR frame where the presence of one is a PHP condition. */
    avatarSlot?: ReactNode
    /** End of the author pill: the follow button for a visitor, plays/likes counters for the owner. */
    pillEndSlot?: ReactNode
    /**
     * Desktop-only action row. Viewer and owner differ in which actions exist and in what order, so
     * the container composes the whole row rather than filling fixed per-action slots.
     */
    actionsSlot: ReactNode
}

export const AudioHeroBar = ({
    authorName,
    authorHref,
    avatarUrl,
    avatarSlot,
    pillEndSlot,
    actionsSlot,
}: TAudioHeroBarProps) => {
    const author = (
        <span className={styles.author}>
            <Avatar className={styles.avatar}>
                {avatarSlot ??
                    (avatarUrl ? (
                        <AvatarImage
                            src={avatarUrl}
                            alt={authorName}
                            width={32}
                            height={32}
                            resize={IMAGE_RESIZE_SPEC}
                        />
                    ) : null)}
                <AvatarFallback />
            </Avatar>
            <span className={styles.authorName}>{authorName}</span>
        </span>
    )

    return (
        <div className={styles.bar} data-test="audio-hero-bar">
            <div className={styles.pill}>
                {authorHref ? (
                    <Link variant="primary" href={authorHref} className={styles.authorLink}>
                        {author}
                    </Link>
                ) : (
                    author
                )}
                {pillEndSlot ? <div className={styles.pillEnd}>{pillEndSlot}</div> : null}
            </div>
            <div className={styles.actions}>{actionsSlot}</div>
        </div>
    )
}
